import { useState } from "react";

import { useMutation, useQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { differenceInSeconds, parseISO } from "date-fns";
import type { TFunction } from "i18next";
import _ from "lodash";
import { Form } from "react-bootstrap";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod/v4";

import { may } from "@/abilities";
import { COMPONENTS, DEFAULT } from "@/components/contacts/components";
import { addDangerFlash, addSuccessFlash } from "@/components/flash/flashReducer";
import TTModal from "@/components/TTModal";
import {
  ContactStatesDocument,
  HistoriesCallResultsDocument,
  MutateContactDocument,
  MutateHistoryDocument,
  MutateTaskDocument,
  MutateTodoDocument,
  type TodoInput,
} from "@/gql/graphql";
import handleError, { MutationError } from "@/handleError";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { deleteInactiveCalls, toggleAddCallHistoryModal } from "@/store/acdReducer";
import { acdSelector, type AcdStateType } from "@/store/acdReducer";
import { sessionSelector } from "@/store/sessionReducer";
import { getAddfields } from "@/utils/contacts";

import Inner from "./Inner";

interface DefaultTask extends CCTaskType {
  caddfields: { [key: string]: unknown };
  statusId?: string;
  skipDetails?: boolean;
  todo?: Partial<TodoInput>;
}

function validationSchema<T = unknown>(ccSchema: Nilable<z.Schema<T>>) {
  return z
    .object({
      subject: z.string().trim().nonempty("acd:call_history_entry.subject_missing"),
      details: z.string().trim(),
      htype: z.literal("CALL"),
      showCustomerContact: z.boolean(),
      customerContact:
        ccSchema ||
        z
          .looseObject({
            caddfields: z.any().nullable(),
            statusId: z.string().nullable(),
            skipDetails: z.boolean().nullable(),
          })
          .nullish(),
      callResultId: z.string().nullable(),
      meta: z.object({
        reached: z.boolean().nullable(),
        direction: z.enum(["INBOUND", "OUTBOUND"]),
        initiator: z.string().trim(),
        recipient: z.string().trim().nullable(),
        connected: z.boolean(),
        started: z.string().trim(),
        stopped: z.string().trim(),
        waitingStopped: z.string().nullable(),
        duration: z.number(),
        talkTime: z.number().nullable(),
        callRecordId: z.string().trim().nullable(),
        hasRecordings: z.boolean(),
      }),
    })
    .check((ctx) => {
      if (typeof ctx.value.meta.reached !== "boolean") {
        ctx.issues.push({
          code: "custom",
          input: ctx.value.meta.reached,
          message: "acd:call_history_entry.reached_missing",
          path: ["meta.reached"],
        });
      }
    })
    .check((ctx) => {
      const skipDetails = (ctx.value.customerContact as any)?.skipDetails;

      if (ctx.value.meta.reached && skipDetails === false && !ctx.value.details) {
        ctx.issues.push({
          code: "custom",
          input: ctx.value.details,
          message: "acd:call_history_entry.details_missing",
          path: ["details"],
        });
      }
    });
}

export type CallHistoryEntry<T = unknown> = z.infer<ReturnType<typeof validationSchema<T>>>;

function initialValues(t: TFunction, acd: AcdStateType): CallHistoryEntry {
  const call = acd.modalCall ? acd.inactiveCalls[acd.modalCall.callGroupId]! : [];

  const firstEvent = call[0];
  const lastEvent = call[call.length - 1];
  // in this case stopped can't be null/undefined
  const stopped = lastEvent ? parseISO(lastEvent.stopped!) : new Date();

  let reached: boolean | null = null;

  if (lastEvent.direction === "INBOUND") {
    reached = lastEvent.connected;
  }

  return {
    subject:
      firstEvent?.direction === "INBOUND"
        ? t("acd:call_history_entry.inbound_call")
        : t("acd:call_history_entry.outbound_call"),
    details: "",
    htype: "CALL",
    showCustomerContact: false,
    customerContact: null,
    callResultId: null,
    meta: {
      reached,
      direction: firstEvent.direction,
      initiator: firstEvent.initiator,
      recipient: firstEvent.recipient,
      connected: lastEvent.connected,
      started: lastEvent.started,
      stopped: lastEvent.stopped!,
      waitingStopped: lastEvent?.waitingStopped,
      duration: differenceInSeconds(stopped, parseISO(lastEvent.started!)),
      talkTime: lastEvent.waitingStopped ? differenceInSeconds(stopped, parseISO(lastEvent.waitingStopped)) : null,
      callRecordId: lastEvent.callRecordId || null,
      hasRecordings: false, // TODO: call recordings are not implemented,
    },
  };
}

export default function CallHistoryEntry() {
  const session = useAppSelector(sessionSelector);
  const acd = useAppSelector(acdSelector);
  const dispatch = useAppDispatch();
  const [CCSchema, setCCSchema] = useState<Nullable<z.Schema<any>>>(null);
  const { t } = useTranslation(["translation", "acd", "customerContacts"]);

  const [mutateHistoryEntry] = useMutation(MutateHistoryDocument, {
    refetchQueries: ["histories"],
  });
  const [taskMutation] = useMutation(MutateTaskDocument);
  const [todoMutation] = useMutation(MutateTodoDocument);
  const [contactMutation] = useMutation(MutateContactDocument);

  const { data } = useQuery(HistoriesCallResultsDocument, {
    variables: {
      customerId: session.currentCustomer.id,
      projectId: session.currentProject.id,
    },
    skip: !may(session, "contacts"),
  });

  const { data: statesData } = useQuery(ContactStatesDocument, {
    variables: {
      customerId: session.currentCustomer.id,
      projectId: session.currentProject.id,
      filters: { allowedAfterCall: true },
    },
    skip: !acd.showCallHistoryModal || !may(session, "contacts"),
  });

  const states = statesData?.contactStates || [];

  const schema = validationSchema(CCSchema).check((ctx) => {
    if (!!data?.historiesCallResults.length && !ctx.value.callResultId) {
      ctx.issues.push({
        code: "custom",
        input: ctx.value.callResultId,
        message: "Bitte geben Sie ein Gesprächsergebnis an!",
        path: ["callResultId"],
      });
    }
  });

  const form = useForm<CallHistoryEntry>({
    resolver: zodResolver(schema),
    defaultValues: initialValues(t, acd),
    mode: "onBlur",
  });

  async function createHistoryEntry(values: CallHistoryEntry) {
    const saveTask =
      COMPONENTS[session.currentCustomer.identifier]?.[session.currentProject.identifier]?.saveTask ||
      COMPONENTS[session.currentCustomer.identifier]?.default?.saveTask ||
      DEFAULT.saveTask;

    if (values.showCustomerContact && saveTask) {
      saveTask(session, undefined, states, values.customerContact, t);
    } else if (values.showCustomerContact) {
      const taskValues = values.customerContact as DefaultTask;
      const contact = taskValues?.contact;
      const todo = taskValues?.todo;
      const caddfields = taskValues?.caddfields;
      const statusId = taskValues?.statusId;

      const customerContact = _.omit(taskValues, ["infos", "contact", "todo", "caddfields", "statusId", "skipDetails"]);

      try {
        const { data } = await taskMutation({
          variables: {
            values: customerContact,
            customerId: session.currentCustomer.id,
            projectId: session.currentProject.id,
          },

          refetchQueries: ["tasks", "histories"],
        });

        if (!data?.mutateTask) {
          throw new MutationError();
        }

        if ((caddfields || statusId) && contact) {
          const attrs = caddfields
            ? {
                ...contact.attrs,
                addfields: { ...getAddfields(contact), ...caddfields },
              }
            : contact.attrs;

          const { data: contactData } = await contactMutation({
            variables: {
              customerId: session.currentCustomer.id,
              projectId: session.currentProject.id,
              id: contact.id,
              contact: {
                attrs,
                statusId: statusId || undefined,
              },
            },
          });

          if (!contactData?.mutateContact) {
            throw new MutationError();
          }
        }

        if (todo?.description) {
          const { data: todoData } = await todoMutation({
            variables: {
              customerId: session.currentCustomer.id,
              projectId: session.currentProject.id,
              values: {
                ...todo,
                contactId: customerContact?.contactId,
                taskId: data.mutateTask.id,
              } as TodoInput, // we cast here because we really don't know better
            },
            refetchQueries: ["histories"],
          });

          if (!todoData?.mutateTodo) {
            throw new MutationError();
          }
        }

        dispatch(addSuccessFlash(t("customerContacts:new_form.created")));
      } catch (e) {
        dispatch(addDangerFlash(t("translation:global.general_error")));
        handleError(e);
      }
    }

    const meta = _.omitBy(values.meta, _.isUndefined);

    const { data } = await mutateHistoryEntry({
      variables: {
        customerId: session.currentCustomer.id,
        projectId: session.currentProject.id,
        contactId: acd.inactiveCallContact!.id,
        history: { ..._.omit(values, ["showCustomerContact", "customerContact"]), meta },
      },
    });

    if (!data?.mutateHistory) {
      throw new MutationError();
    }

    const callback =
      COMPONENTS[session.currentCustomer.identifier]?.[session.currentProject.identifier]?.["histories/callback"] ||
      COMPONENTS[session.currentCustomer.identifier]?.default?.["histories/callback"] ||
      DEFAULT["histories/callback"];

    if (callback) {
      callback(session, acd.inactiveCallContact!, data.mutateHistory);
    }

    dispatch(toggleAddCallHistoryModal());
    dispatch(deleteInactiveCalls());

    window.dispatchEvent(new CustomEvent("tt:callHistoryCreated", { detail: data.mutateHistory }));
  }

  return (
    <TTModal show={acd.showCallHistoryModal && !!acd.inactiveCallContact} backdrop="static" size="lg">
      <Form onSubmit={form.handleSubmit(createHistoryEntry)}>
        <FormProvider {...form}>
          <Inner setCCSchema={setCCSchema} callResults={data?.historiesCallResults || []} states={states} />
        </FormProvider>
      </Form>
    </TTModal>
  );
}
